import { metadata } from 'store/metadata/metadataApi';
import { getAuthToken } from 'utils/auth';
import { store, type AppDispatch } from 'store';

const baseUrlCache = new Map<string, { url: string; timestamp: number }>();
const CACHE_TTL = 300_000;

export const resolveBaseUrl = async (targetSystem: string, dispatch: AppDispatch): Promise<string> => {
    const cacheKey = targetSystem || '__default__';
    const cached = baseUrlCache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_TTL) return cached.url;

    const result = await dispatch(
        metadata.endpoints.getInformationAboutALocationMetaLocationPost.initiate(
            { locationInfoModel: { target_system: targetSystem } },
            { track: false }
        )
    );

    if ('error' in result) throw new Error(`Location resolution failed for "${targetSystem}"`);

    const resolvedUrl = (result.data as { info?: { transfer_api_url?: string } } | undefined)?.info?.transfer_api_url?.replace(
        /\/$/,
        ''
    );

    if (!resolvedUrl) throw new Error(`No transfer_api_url returned for "${targetSystem}"`);

    baseUrlCache.set(cacheKey, { url: resolvedUrl, timestamp: now });
    return resolvedUrl;
};

export const hpcUpload = (
    file: File,
    targetSystem: string,
    params: Record<string, string>,
    onProgress?: (progress: number) => void
): { promise: Promise<void>; abort: () => void } => {
    const abortController = new AbortController();
    const xhr = new XMLHttpRequest();

    const executeUpload = async (): Promise<void> => {
        const baseUrl = await resolveBaseUrl(targetSystem, store.dispatch);
        const token = await getAuthToken();

        return new Promise<void>((resolve, reject) => {
            xhr.upload.addEventListener('progress', e => {
                if (e.lengthComputable && onProgress) {
                    onProgress((e.loaded / e.total) * 100);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    try {
                        const err = JSON.parse(xhr.responseText) as Record<string, string>;
                        reject(new Error(err?.detail || `Upload failed: ${xhr.status}`));
                    } catch {
                        reject(new Error(`Upload failed: ${xhr.status}`));
                    }
                }
            });

            xhr.addEventListener('error', () => reject(new Error('Network error')));
            xhr.addEventListener('abort', () => reject(new Error('Aborted')));
            abortController.signal.addEventListener('abort', () => xhr.abort());

            const uploadUrl = `${baseUrl}/hpc/upload?${new URLSearchParams(params).toString()}`;
            xhr.open('POST', uploadUrl, true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(file);
        });
    };

    return { promise: executeUpload(), abort: () => abortController.abort() };
};

export type UploadProgressInfo = {
    uploadedSize: number;
    totalSize: number;
    percentage: number;
    speed: number;
    timeRemaining: number | null;
};

export const streamingUpload = async (
    file: File,
    metadataData: Record<string, unknown>,
    options?: {
        onProgress?: (info: UploadProgressInfo) => void;
        onLog?: (message: string) => void;
        signal?: AbortSignal;
    }
): Promise<void> => {
    const targetSystem = (metadataData.target_system as string) ?? '';
    const baseUrl = await resolveBaseUrl(targetSystem, store.dispatch);
    const token = await getAuthToken();
    const endpoint = `${baseUrl}/upload`;

    options?.onLog?.(`📡 Upload endpoint: ${endpoint}`);

    const isZip = file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip');
    const contentType = isZip ? 'application/zip' : 'application/octet-stream';

    options?.onLog?.('📥 Starting standard stream upload...');

    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        const onAbort = () => {
            xhr.abort();
        };

        if (options?.signal) {
            if (options.signal.aborted) {
                return reject(new Error('Aborted'));
            }
            options.signal.addEventListener('abort', onAbort);
        }

        const startTime = Date.now();

        xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable && options?.onProgress) {
                const currentOffset = e.loaded;
                const elapsed = (Date.now() - startTime) / 1000;
                const speed = elapsed > 0 ? currentOffset / elapsed : 0;
                options.onProgress({
                    uploadedSize: currentOffset,
                    totalSize: file.size,
                    percentage: (currentOffset / file.size) * 100,
                    speed,
                    timeRemaining: speed > 0 ? (file.size - currentOffset) / speed : null,
                });
            }
        });

        xhr.addEventListener('load', () => {
            if (options?.signal) {
                options.signal.removeEventListener('abort', onAbort);
            }

            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const responseData = JSON.parse(xhr.responseText) as Record<string, unknown>;
                    if (responseData.dataset_id) {
                        options?.onLog?.(`✅ Dataset created with ID: ${responseData.dataset_id as string}`);
                    }
                    options?.onLog?.('🎉 Upload complete');
                    resolve();
                } catch (e) {
                    reject(new Error('Failed to parse successful response'));
                }
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
            }
        });

        xhr.addEventListener('error', () => {
            if (options?.signal) {
                options.signal.removeEventListener('abort', onAbort);
            }
            reject(new Error('Network error'));
        });

        xhr.addEventListener('abort', () => {
            if (options?.signal) {
                options.signal.removeEventListener('abort', onAbort);
            }
            reject(new Error('Aborted'));
        });

        const jsonString = JSON.stringify(metadataData);
        const uint8Array = new TextEncoder().encode(jsonString);
        const base64Metadata = btoa(Array.from(uint8Array).map(b => String.fromCharCode(b)).join(''));

        xhr.open('POST', endpoint, true);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.setRequestHeader('Upload-Metadata', base64Metadata);
        if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(file);
    });
};